import { useState, useEffect, useCallback } from 'react';
import { Button, Tooltip, Spin } from 'antd';
import {
    CloseOutlined,
    SoundOutlined,
    AudioMutedOutlined,
    FullscreenOutlined,
    FullscreenExitOutlined,
} from '@ant-design/icons';
import { FaTrophy } from 'react-icons/fa';
import { IAwardWithWinner } from '../../Steps/ImportData/useImportData';
import { IRulesGiveaway } from '../../../../../interfaces';
import useAuth from '../../../../../hooks/useAuth';
import ImagemLogo from '../../../../../assets/images/logo-lateral.png';
import { urlHttpGov } from '../../../../../services/urls';
import { soundFx } from './utils/soundSynthesizer';
import DrawIntro from './components/DrawIntro';
import DrawStage from './components/DrawStage';
import DrawSummary from './components/DrawSummary';
import './styles.css';

interface GiveawayLiveDrawModalProps {
    open: boolean;
    onClose: () => void;
    giveawayTitle?: string;
    giveawayDate?: string | Date;
    attemptNumber?: number;
    awards?: IAwardWithWinner[];
    rules?: IRulesGiveaway;
    loading?: boolean;
}

export default function GiveawayLiveDrawModal({
    open,
    onClose,
    giveawayTitle,
    giveawayDate,
    attemptNumber,
    awards = [],
    rules,
    loading = false,
}: GiveawayLiveDrawModalProps) {
    const { activeCity } = useAuth();
    const cityName = activeCity?.name;

    const cityLogoUrl = activeCity?.logo_county
        ? `${urlHttpGov}/logo_county/${activeCity.logo_county}`
        : activeCity?.logo_light
            ? `${urlHttpGov}/logo_county/${activeCity.logo_light}`
            : null;

    const formatCityTitle = (name?: string) => {
        if (!name) return 'Programa Nota Município';
        const trimmed = name.trim();
        if (trimmed.toLowerCase().startsWith('prefeitura')) {
            return trimmed;
        }
        return `Prefeitura de ${trimmed}`;
    };

    const [viewMode, setViewMode] = useState<'intro' | 'stage' | 'summary'>('intro');
    const [isMuted, setIsMuted] = useState<boolean>(soundFx.getIsMuted());
    const [isFullscreen, setIsFullscreen] = useState<boolean>(false);

    // Reset to intro when modal opens
    useEffect(() => {
        if (open) {
            setViewMode('intro');
        }
    }, [open]);

    // Handle ESC key to close
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && open) {
                onClose();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [open, onClose]);

    const handleToggleMute = useCallback(() => {
        const newMuteState = soundFx.toggleMute();
        setIsMuted(newMuteState);
    }, []);

    const handleToggleFullscreen = useCallback(() => {
        if (!document.fullscreenElement) {
            document.documentElement.requestFullscreen().catch(() => { });
            setIsFullscreen(true);
        } else {
            if (document.exitFullscreen) {
                document.exitFullscreen().catch(() => { });
            }
            setIsFullscreen(false);
        }
    }, []);

    if (!open) return null;

    // Filter awards that exist
    const validAwards = awards.filter(
        (a: any) => a && (a.winner || a.winning_ticket_id || a.winning_ticket_auto_id || a.app_user)
    );

    return (
        <div className="municipal-modal-overlay">
            {/* <div
                className="municipal-waves-bg"
                style={{ backgroundImage: `url(${ImageWaves})` }}
            /> */}

            <header className="municipal-header-bar">
                <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                    <img
                        src={ImagemLogo}
                        alt="Nota Município"
                        style={{ height: 30, objectFit: 'contain' }}
                    />

                    {cityLogoUrl && (
                        <>
                            <div style={{ height: 22, width: 1, background: 'rgba(255, 255, 255, 0.2)' }} />
                            <img
                                src={cityLogoUrl}
                                alt="Prefeitura"
                                style={{ height: 28, maxWidth: 100, objectFit: 'contain' }}
                            />
                        </>
                    )}

                    <div style={{ height: 24, width: 1, background: 'rgba(255, 255, 255, 0.18)' }} />

                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <span style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', letterSpacing: 0.2 }}>
                            {formatCityTitle(cityName)}
                        </span>
                        <span style={{ fontSize: 11, color: '#2563eb', fontWeight: 600 }}>
                            {giveawayTitle}
                        </span>
                    </div>
                </div>

                {/* Right controls */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Tooltip title={isMuted ? 'Ativar efeitos sonoros' : 'Desativar som'}>
                        <Button
                            type="text"
                            icon={isMuted ? <AudioMutedOutlined style={{ color: '#ef4444' }} /> : <SoundOutlined style={{ color: '#2563eb' }} />}
                            onClick={handleToggleMute}
                            style={{
                                color: '#475569',
                                background: '#f1f5f9',
                                border: '1px solid #e2e8f0',
                                borderRadius: 8,
                            }}
                        />
                    </Tooltip>

                    <Tooltip title={isFullscreen ? 'Sair da Tela Cheia' : 'Modo Tela Cheia'}>
                        <Button
                            type="text"
                            icon={isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
                            onClick={handleToggleFullscreen}
                            style={{
                                color: '#475569',
                                background: '#f1f5f9',
                                border: '1px solid #e2e8f0',
                                borderRadius: 8,
                            }}
                        />
                    </Tooltip>

                    <Tooltip title="Fechar Apuração (Esc)">
                        <Button
                            type="text"
                            icon={<CloseOutlined />}
                            onClick={onClose}
                            style={{
                                color: '#dc2626',
                                background: '#fee2e2',
                                border: '1px solid #fecaca',
                                borderRadius: 8,
                            }}
                        />
                    </Tooltip>
                </div>
            </header>

            {/* Stage Body */}
            <main style={{ flex: 1, position: 'relative', zIndex: 10, display: 'flex', alignItems: 'start', justifyContent: 'center' }}>
                {loading ? (
                    <div style={{ textAlign: 'center', padding: '60px 0' }}>
                        <Spin size="large" />
                        <div style={{ marginTop: 16, color: '#64748b', fontSize: 14 }}>
                            Carregando dados da apuração oficial...
                        </div>
                    </div>
                ) : validAwards.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '60px 20px' }}>
                        <FaTrophy style={{ fontSize: 48, color: '#f59e0b', marginBottom: 16 }} />
                        <div style={{ fontSize: 18, fontWeight: 700, color: '#0f172a', marginBottom: 8 }}>
                            Nenhum bilhete homologado encontrado
                        </div>
                        <div style={{ color: '#64748b', fontSize: 14, marginBottom: 20 }}>
                            Não há registros de bilhetes premiados para esta apuração.
                        </div>
                        <Button type="primary" onClick={onClose}>
                            Fechar
                        </Button>
                    </div>
                ) : (
                    <>
                        {viewMode === 'intro' && (
                            <DrawIntro
                                giveawayTitle={giveawayTitle}
                                giveawayDate={giveawayDate}
                                cityName={cityName}
                                attemptNumber={attemptNumber}
                                awards={validAwards}
                                onStartDraw={() => setViewMode('stage')}
                                onSkipToSummary={() => setViewMode('summary')}
                            />
                        )}

                        {viewMode === 'stage' && (
                            <DrawStage
                                awards={validAwards}
                                rules={rules}
                                cityName={cityName}
                                onFinishAll={() => setViewMode('summary')}
                            />
                        )}

                        {viewMode === 'summary' && (
                            <DrawSummary
                                giveawayTitle={giveawayTitle}
                                cityName={cityName}
                                attemptNumber={attemptNumber}
                                awards={validAwards}
                                rules={rules}
                                onRestartDraw={() => setViewMode('stage')}
                                onClose={onClose}
                            />
                        )}
                    </>
                )}
            </main>
        </div>
    );
}
